import { useEffect, useRef, useState } from "react";
import { absoluteTime } from "../components/lot-bits.tsx";
import { api } from "../lib/api.ts";
import { useSources } from "../lib/queries.ts";
import { useNow } from "../lib/store.ts";

const REACH: Record<string, string> = {
  http: "Hammer checks it at noon",
  client: "Your browser or Mac, at noon",
  capture: "Bookmarklet or your Mac",
};

export function Sources() {
  const sources = useSources().data ?? [];
  const now = useNow();
  return (
    <div className="grid gap-8 max-w-[64rem]">
      <section className="grid gap-3">
        <h1 className="heading text-[26px]">Sources</h1>
        <p className="muted max-w-[62ch]">
          How each auction site reaches Hammer. Lots are checked once a day at noon Eastern. Sites that refuse automated checks are never worked around: they update from your browser while Hammer is open, or when you click the bookmarklet on a lot page.
        </p>
        {sources.length ? (
          <div className="overflow-x-auto">
            <table className="data min-w-[44rem]">
              <thead>
                <tr><th>Site</th><th>How it updates</th><th className="r">Open lots</th><th className="r">Checks OK, 24h</th><th className="r">Typical time</th><th>Last problem</th></tr>
              </thead>
              <tbody>
                {sources.map((s) => {
                  const rate = s.attempts24h ? s.ok24h / s.attempts24h : null;
                  return (
                    <tr key={s.source}>
                      <td className="font-[560]">{s.label}</td>
                      <td className="muted">{REACH[s.reach]}</td>
                      <td className="r num">{s.activeLots}</td>
                      <td className={`r num ${rate != null && rate < 0.8 ? "text-red" : ""}`}>{s.attempts24h ? `${s.ok24h} of ${s.attempts24h}` : "—"}</td>
                      <td className="r num muted">{s.medianMs != null ? `${(s.medianMs / 1000).toFixed(1)}s` : "—"}</td>
                      <td className="text-[13px]">{s.lastError ? <><span className="muted">{absoluteTime(s.lastError.at, now)}: </span>{s.lastError.detail ?? s.lastError.outcome}</> : <span className="muted">None</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <p className="muted">No lots yet, so no sources.</p>}
      </section>

      <Bookmarklet />
      <TestUrl />
    </div>
  );
}

function Bookmarklet() {
  const link = useRef<HTMLAnchorElement>(null);
  const [code, setCode] = useState<string | null>(null);
  const [hint, setHint] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    fetch("/bookmarklet.js")
      .then((r) => r.text())
      .then((src) => setCode("javascript:" + encodeURIComponent(src.replace(/\n\s*/g, ""))))
      .catch(() => {});
  }, []);
  // React 19 replaces javascript: URLs passed as href with a dummy that only throws, which is what a dragged
  // bookmark would then contain. Setting the attribute on the element directly keeps the real code.
  useEffect(() => {
    if (code && link.current) link.current.setAttribute("href", code);
  }, [code]);

  return (
    <section className="grid gap-3 max-w-[62ch]">
      <h2 className="heading text-[21.6px]">Bookmarklet</h2>
      <p>On any auction lot page, click it to add the lot to Hammer or update its price and end time. It works on sites that block Hammer, because it reads the page you're already looking at.</p>
      <div className="flex flex-wrap gap-2 items-center">
        {code ? (
          <a ref={link} className="btn btn-primary" draggable onClick={(e) => { e.preventDefault(); setHint(true); }}>Add to Hammer</a>
        ) : <span className="muted">Loading…</span>}
        <button className="btn" disabled={!code} onClick={async () => { await navigator.clipboard.writeText(code!); setCopied(true); setTimeout(() => setCopied(false), 2500); }}>
          {copied ? "Copied" : "Copy bookmarklet"}
        </button>
      </div>
      {hint && <p className="text-amber text-[14px]" role="status">Don't click it here: drag the button onto your bookmarks bar, then click the bookmark when you're on a lot page.</p>}
      <details className="text-[14px]" open={hint}>
        <summary className="cursor-pointer muted">How to install it</summary>
        <div className="grid gap-3 mt-2">
          <div>
            <strong className="font-[600]">Safari on a Mac</strong>
            <ol className="list-decimal pl-5 mt-1 grid gap-0.5">
              <li>Show the Favorites bar: View, then Show Favorites Bar (⇧⌘B).</li>
              <li>Drag the blue "Add to Hammer" button onto it.</li>
              <li>If you dragged an earlier version, delete that one: it doesn't work.</li>
            </ol>
          </div>
          <div>
            <strong className="font-[600]">Chrome, Edge, Firefox</strong>
            <p className="mt-1">Show the bookmarks bar (⇧⌘B on a Mac, Ctrl+Shift+B on Windows) and drag the button onto it.</p>
          </div>
          <div>
            <strong className="font-[600]">iPhone or iPad</strong>
            <ol className="list-decimal pl-5 mt-1 grid gap-0.5">
              <li>Tap "Copy bookmarklet" above.</li>
              <li>Bookmark any page (Share, then Add Bookmark) and name it "Add to Hammer".</li>
              <li>Open Bookmarks, tap Edit, tap the new bookmark, and replace its address with what you copied.</li>
              <li>On a lot page, tap the address bar, type "Add to Hammer", and tap the bookmark.</li>
            </ol>
          </div>
        </div>
      </details>
    </section>
  );
}

function TestUrl() {
  const [url, setUrl] = useState("");
  const [out, setOut] = useState<string | null>(null);
  return (
    <section className="grid gap-3 max-w-[62ch]">
      <h2 className="heading text-[21.6px]">Test a lot link</h2>
      <p className="muted">See which site reader handles a link and what it reads, without saving anything.</p>
      <form className="flex gap-2" onSubmit={async (e) => { e.preventDefault(); setOut("Reading…"); try { setOut(JSON.stringify(await api("/sources/test", { method: "POST", body: { url } }), null, 2)); } catch (err) { setOut((err as Error).message); } }}>
        <input className="field" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} aria-label="Lot link" />
        <button className="btn">Test</button>
      </form>
      {out && <pre className="panel text-[12.5px] overflow-auto max-h-80 whitespace-pre-wrap">{out}</pre>}
    </section>
  );
}
